import { useState, useEffect } from 'react'
import { useAuth } from '@/context/AuthContext'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { Slider } from '@/components/ui/slider'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'

import { PlanBadge } from '@/components/shared/PlanBadge'
import { api } from '@/lib/api'
import { isSafeRedirectUrl } from '@/lib/security'
import { CreditCard, Zap, Loader2, AlertCircle, Receipt, ExternalLink, XCircle, Mail, Globe, Clock } from 'lucide-react'
import { toast } from 'sonner'

interface AccountMe {
  id: string
  email: string
  display_name: string | null
  plan: string
  status: string
  current_period_end: string | null
  usage: {
    domains_used: number
    domains_limit: number
    mailboxes_used: number
    mailboxes_limit: number
    emails_sent_this_year: number
    emails_limit: number
    storage_used_mb: number
    storage_limit_mb: number
    extra_emails_remaining: number
    extra_storage_gb: number
    extra_domain_count: number
  }
}

interface Subscription {
  status: string
  plan: string
  current_period_start: string | null
  current_period_end: string | null
  grace_period_end: string | null
  payment_failed_at: string | null
  cancelled_at: string | null
  refund_eligible: boolean
  refund_reason: string | null
  refund_amount_cents: number | null
  refund_currency: string | null
  refund_eligible_until: string | null
  refund_status: 'pending' | 'review' | 'succeeded' | 'failed' | null
}

type CancellationFeedback =
  | 'too_expensive'
  | 'missing_features'
  | 'switched_service'
  | 'unused'
  | 'customer_service'
  | 'low_quality'
  | 'too_complex'
  | 'other'

const CANCELLATION_REASONS: Array<{ value: CancellationFeedback; label: string }> = [
  { value: 'too_expensive', label: 'Too expensive' },
  { value: 'missing_features', label: 'Missing features I need' },
  { value: 'switched_service', label: 'Switching to another service' },
  { value: 'unused', label: 'Not using it enough' },
  { value: 'customer_service', label: 'Support experience' },
  { value: 'low_quality', label: 'Reliability or quality issues' },
  { value: 'too_complex', label: 'Too difficult to use' },
  { value: 'other', label: 'Other' },
]

interface Plan {
  id: string
  slug: string
  name: string
  description: string | null
  price_cents: number
  currency: string
  billing_period: string
  limits: Record<string, number>
  features: Record<string, unknown>
  is_default: boolean
}

interface Payment {
  id: string
  date: string
  amount_cents: number
  currency: string
  status: string
  payment_type: string
  description: string | null
  invoice_url: string | null
}

interface OveragePack {
  pack_type: string
  name: string
  description: string | null
  price_cents: number
  currency: string
  credits: Record<string, number>
  billing_frequency?: string
  billing_period?: string
  min_quantity?: number
  max_quantity?: number
}

interface AddOn {
  id: string
  pack_type: string
  quantity: number
  status: string
  price_cents_per_unit: number
  total_cents: number
  currency: string
  current_period_end: string | null
  cancelled_at: string | null
}

const LIMIT_LABELS: Record<string, string> = {
  domains: 'Domains',
  mailboxes: 'Mailboxes',
  emails_yearly: 'Emails / Year',
  storage_mb: 'Storage',
  max_attachment_mb: 'Max Attachment',
  webhooks: 'Webhooks',
  api_daily: 'API Calls / Day',
  templates: 'Templates',
  aliases_per_domain: 'Aliases / Domain',
  api_keys: 'API Keys',
  rate_limit_per_minute: 'Rate Limit / Min',
  rate_limit_ip_per_minute: 'IP Rate Limit / Min',
}

function formatPrice(cents: number, currency: string) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD',
    minimumFractionDigits: 0,
  }).format(cents / 100)
}

function formatLimit(value: number) {
  if (value < 0) return 'Unlimited'
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`
  if (value >= 1000) return `${(value / 1000).toFixed(0)}K`
  return value.toLocaleString()
}

function getApiErrorMessage(error: any, fallback: string): string {
  const detail = error?.response?.data?.detail
  if (typeof detail === 'string') return detail
  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => typeof item?.msg === 'string' ? item.msg : null)
      .filter(Boolean)
    if (messages.length) return messages.join('. ')
  }
  if (detail && typeof detail === 'object') {
    if (typeof detail.message === 'string') return detail.message
    if (typeof detail.msg === 'string') return detail.msg
  }
  return fallback
}

export function Billing() {
  const { user } = useAuth()
  const [account, setAccount] = useState<AccountMe | null>(null)
  const [subscription, setSubscription] = useState<Subscription | null>(null)
  const [plans, setPlans] = useState<Plan[]>([])
  const [payments, setPayments] = useState<Payment[]>([])
  const [packs, setPacks] = useState<OveragePack[]>([])
  const [addons, setAddons] = useState<AddOn[]>([])
  const [overageEnabled, setOverageEnabled] = useState<boolean>(true)
  const [loading, setLoading] = useState(true)
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null)
  const [cancelLoading, setCancelLoading] = useState(false)
  const [showCancelDialog, setShowCancelDialog] = useState(false)
  const [cancelStep, setCancelStep] = useState<1 | 2>(1)
  const [cancelFeedback, setCancelFeedback] = useState<CancellationFeedback | ''>('')
  const [cancelComment, setCancelComment] = useState('')
  const [cancelError, setCancelError] = useState<string | null>(null)
  const [showRefundDialog, setShowRefundDialog] = useState(false)
  const [refundLoading, setRefundLoading] = useState(false)
  const [customCheckoutLoading, setCustomCheckoutLoading] = useState<string | null>(null)
  const [storageGb, setStorageGb] = useState<number>(10)

  useEffect(() => {
    const storageAddon = addons.find((a) => a.pack_type === 'storage_per_gb' && a.status === 'active' && !a.cancelled_at)
    const storagePack = packs.find((p) => p.pack_type === 'storage_per_gb')
    setStorageGb(storageAddon?.quantity ?? storagePack?.min_quantity ?? 10)
  }, [addons, packs])

  const fetchBillingData = async () => {
    const [accountRes, subRes, plansRes, paymentsRes, packsRes, addonsRes] = await Promise.all([
      api.get('/account/me'),
      api.get('/billing/subscription'),
      api.get('/billing/plans'),
      api.get('/billing/payments'),
      api.get('/billing/overage-packs'),
      api.get('/billing/addons'),
    ])
    setAccount(accountRes.data)
    setSubscription(subRes.data)
    setPlans(plansRes.data.plans || [])
    setPayments(paymentsRes.data.payments || [])
    setPacks(packsRes.data.packs || [])
    setOverageEnabled(packsRes.data.enabled !== false)
    setAddons(addonsRes.data.addons || [])
  }

  useEffect(() => {
    fetchBillingData()
      .catch(() => toast.error('Failed to load billing data'))
      .finally(() => setLoading(false))
  }, [])

  const plan = subscription?.plan || account?.plan || user?.plan || 'starter'
  const isOnboarding = (subscription?.status === 'pending') || (account?.status === 'onboarding') || (user?.status === 'onboarding')
  const currentPlan = plans.find((p) => p.slug === plan) || plans[0]
  const usage = account?.usage
  const canCancel = !isOnboarding && subscription && subscription.status === 'active'

  const handleSubscribe = async (targetPlan: string) => {
    setCheckoutLoading(targetPlan)
    try {
      const res = await api.post('/billing/checkout', { plan: targetPlan })
      const { checkout_url } = res.data
      if (checkout_url && isSafeRedirectUrl(checkout_url)) {
        window.location.href = checkout_url
      } else {
        toast.error('Checkout URL not available or unsafe')
      }
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to start checkout')
    } finally {
      setCheckoutLoading(null)
    }
  }

  // CHECKPOINT: PRD-13 FR-13.7 — Customer can purchase storage add-on amount.
  const handlePurchaseCustom = async (packType: string, quantity: number) => {
    setCustomCheckoutLoading(packType)
    try {
      const res = await api.post('/billing/checkout/overage', {
        pack_type: packType,
        quantity,
      })
      const { checkout_url, status, message } = res.data
      if (checkout_url && isSafeRedirectUrl(checkout_url)) {
        window.location.href = checkout_url
        return
      }
      if (status === 'ok') {
        toast.success(message || 'Add-on change initiated.', {
          description: 'Dodo Payments is confirming the payment. It will activate shortly.',
        })
        await fetchBillingData()
        return
      }
      toast.error('Unexpected response from billing.')
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to start checkout')
    } finally {
      setCustomCheckoutLoading(null)
    }
  }

  const handleChangeAddon = async (addonId: string, quantity: number) => {
    setCustomCheckoutLoading(addonId)
    try {
      await api.post(`/billing/addons/${addonId}/change`, { quantity })
      toast.success('Add-on quantity change initiated.', {
        description: 'Dodo Payments is confirming the payment. It will activate shortly.',
      })
      await fetchBillingData()
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to update add-on')
    } finally {
      setCustomCheckoutLoading(null)
    }
  }

  const handleCancelAddon = async (addonId: string) => {
    const usageMb = account?.usage?.storage_used_mb ?? 0
    const baseLimitMb = currentPlan?.limits?.storage_mb ?? 0
    const exceedsBase = baseLimitMb >= 0 && usageMb > baseLimitMb
    if (exceedsBase) {
      if (!window.confirm('Cancelling storage will remove your extra storage at the end of this billing period. Your current usage exceeds your base plan storage, so data may become restricted. Continue?')) {
        return
      }
    } else {
      if (!window.confirm('Cancel this add-on? It remains active until the end of the current billing period.')) {
        return
      }
    }
    setCustomCheckoutLoading(addonId)
    try {
      await api.post(`/billing/addons/${addonId}/cancel`, { reason: 'customer_request' })
      toast.success('Add-on cancelled.')
      await fetchBillingData()
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to cancel add-on')
    } finally {
      setCustomCheckoutLoading(null)
    }
  }

  // CHECKPOINT: PRD-13 FR-13.8.4 — Customer can cancel subscription at period end.
  const handleCancelSubscription = async () => {
    if (!cancelFeedback) {
      setCancelError('Choose the reason that best matches your decision.')
      return
    }
    if (cancelFeedback === 'other' && !cancelComment.trim()) {
      setCancelError('Tell us a little more when choosing Other.')
      return
    }
    setCancelError(null)
    setCancelLoading(true)
    try {
      const res = await api.post('/billing/subscription/cancel', {
        reason: 'customer_request',
        cancellation_feedback: cancelFeedback,
        cancellation_comment: cancelComment.trim() || null,
      })
      toast.success('Subscription cancelled. You keep access until the end of the current period.')
      await fetchBillingData()
      setShowCancelDialog(false)
      setCancelStep(1)
      setCancelFeedback('')
      setCancelComment('')
      if (res.data.refund_eligible) {
        window.setTimeout(() => setShowRefundDialog(true), 150)
      }
    } catch (err: any) {
      toast.error(getApiErrorMessage(err, 'Failed to cancel subscription'))
    } finally {
      setCancelLoading(false)
    }
  }

  // CHECKPOINT: PRD-13 FR-13.8.1 Refund requests use account closure.
  const handleRequestRefund = async () => {
    setRefundLoading(true)
    try {
      const res = await api.post('/account/close', {
        reason: 'customer_request',
        confirm_delete: true,
      })
      toast.success(res.data.message || 'Refund requested and account closure scheduled.')
      setShowRefundDialog(false)
      await fetchBillingData()
    } catch (err: any) {
      toast.error(getApiErrorMessage(err, 'Failed to request refund'))
    } finally {
      setRefundLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  const featureKeys = Array.from(new Set(plans.flatMap((p) => Object.keys(p.limits || {})))).filter((key) =>
    plans.some((p) => p.limits?.[key] !== undefined)
  )

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Usage & Billing</h1>
        <p className="text-sm text-muted-foreground mt-1">Monitor usage and manage your plan</p>
      </div>

      {/* Current Plan Card */}
      <Card className={isOnboarding ? "border-amber-500/20 bg-amber-500/5" : "bg-gradient-to-br from-violet-500/10 to-purple-600/10 border-violet-500/20"}>
        <CardContent className="p-6">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              {isOnboarding ? (
                <>
                  <div className="flex items-center gap-2 mb-1">
                    <h2 className="text-lg font-bold">Not Subscribed</h2>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/20">Preview</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    You're browsing the {currentPlan?.name || plan} plan preview. Subscribe to unlock all features.
                  </p>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-2 mb-1">
                    <h2 className="text-lg font-bold">{currentPlan?.name || plan} Plan</h2>
                    <PlanBadge plan={plan} />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {currentPlan && (
                      <>
                        {formatPrice(currentPlan.price_cents, currentPlan.currency)} / {currentPlan.billing_period}
                      </>
                    )}
                    {account?.current_period_end && (
                      <> · {subscription?.status === 'cancelled' ? 'Access until' : 'Renews'} {new Date(account.current_period_end).toLocaleDateString()}</>
                    )}
                  </p>
                </>
              )}
              {account?.status === 'grace_period' && (
                <div className="flex items-center gap-2 mt-2 text-amber-400 text-sm">
                  <AlertCircle className="h-4 w-4" />
                  <span>Your account is in a grace period. Please update payment.</span>
                </div>
              )}
              {subscription?.status === 'cancelled' && (
                <div className="mt-3 max-w-xl rounded-lg border border-amber-500/25 bg-amber-500/8 p-3 text-sm">
                  <div className="flex items-center gap-2 font-medium text-amber-300">
                    <Clock className="h-4 w-4" />
                    <span>
                      {subscription.current_period_end && new Date(subscription.current_period_end) > new Date()
                        ? 'Cancelling'
                        : 'Cancelled'}
                    </span>
                  </div>
                  <p className="mt-1 text-muted-foreground">
                    No more renewals. You keep access until{' '}
                    {subscription.current_period_end
                      ? new Date(subscription.current_period_end).toLocaleDateString()
                      : 'the end of the current period'}.
                  </p>
                  {subscription.refund_status && (
                    <p className="mt-2 text-xs text-muted-foreground">
                      Refund status: <span className="font-medium capitalize text-foreground">{subscription.refund_status}</span>
                    </p>
                  )}
                </div>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {plans.map((p) => {
                const isCurrent = p.slug === plan && !isOnboarding
                return (
                  <Button
                    key={p.slug}
                    size="sm"
                    variant={isCurrent ? 'outline' : 'default'}
                    className={!isCurrent ? 'bg-gradient-to-r from-violet-500 to-purple-600' : ''}
                    onClick={() => handleSubscribe(p.slug)}
                    disabled={checkoutLoading === p.slug || isCurrent}
                  >
                    {checkoutLoading === p.slug ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        {isOnboarding ? `Subscribe to ${p.name}` : isCurrent ? 'Current Plan' : `Switch to ${p.name}`}
                        {!isCurrent && <Zap className="ml-2 h-3 w-3" />}
                      </>
                    )}
                  </Button>
                )
              })}
              {canCancel && (
                <Button
                  size="sm"
                  variant="outline"
                  className="border-red-500/20 text-red-400 hover:bg-red-500/10 hover:text-red-300"
                  onClick={() => {
                    setCancelError(null)
                    setCancelStep(1)
                    setShowCancelDialog(true)
                  }}
                  disabled={cancelLoading}
                >
                  {cancelLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      Cancel Subscription <XCircle className="ml-2 h-3 w-3" />
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {subscription?.status === 'cancelled' && subscription.refund_eligible && !subscription.refund_status && (
        <Card className="border-emerald-500/30 bg-emerald-500/5">
          <CardContent className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-400">
                <Receipt className="h-5 w-5" />
              </div>
              <div>
                <p className="font-semibold text-foreground">You qualify for a full refund</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Request {formatPrice(subscription.refund_amount_cents || 0, subscription.refund_currency || 'USD')}
                  {subscription.refund_eligible_until
                    ? ` by ${new Date(subscription.refund_eligible_until).toLocaleDateString()}`
                    : ' within your 30-day money-back window'}.
                </p>
              </div>
            </div>
            <Button variant="destructive" onClick={() => setShowRefundDialog(true)}>
              Request full refund
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Usage Limits */}
      <div className="grid gap-4">
        <h3 className="text-sm font-medium">Plan Limits</h3>
        {usage ? (
          <>
            <LimitRow label="Domains" used={usage.domains_used} limit={usage.domains_limit} />
            <LimitRow label="Mailboxes" used={usage.mailboxes_used} limit={usage.mailboxes_limit} />
            <LimitRow label="Emails / Year" used={usage.emails_sent_this_year} limit={usage.emails_limit} />
            {/* CHECKPOINT: PRD-13 — Pooled storage wording on billing limits. */}
            <LimitRow label="Pooled Storage (MB)" used={Math.round(usage.storage_used_mb)} limit={usage.storage_limit_mb} />
          </>
        ) : (
          <p className="text-sm text-muted-foreground">Usage data unavailable</p>
        )}
      </div>

      {overageEnabled ? (
        <>
          {/* CHECKPOINT: PRD-13 FR-13.7 — Yearly storage add-on slider */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Zap className="h-4 w-4 text-muted-foreground" />
                Extra Storage Add-on
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                Extra storage is a yearly add-on attached to your base plan. Cancelling it does not cancel your PurpleToad Mail plan.
              </p>
              <StorageAddOnControl
                packs={packs}
                addons={addons}
                value={storageGb}
                onChange={setStorageGb}
                current={account?.usage?.extra_storage_gb ?? 0}
                loadingId={customCheckoutLoading}
                onPurchase={handlePurchaseCustom}
                onChangeAddon={handleChangeAddon}
                onCancelAddon={handleCancelAddon}
              />
            </CardContent>
          </Card>

          {/* CHECKPOINT: PRD-13 FR-13.7 — One-time email packs and extra domain add-ons */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                One-time Add-ons
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                Purchase extra email credits or additional domain slots. These are one-time charges and do not renew.
              </p>
              <OneTimePackGrid
                packs={packs}
                loadingId={customCheckoutLoading}
                onPurchase={handlePurchaseCustom}
              />
            </CardContent>
          </Card>
        </>
      ) : (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Zap className="h-4 w-4 text-muted-foreground" />
              Add-ons
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Extra storage, email packs, and additional domain slots are coming soon.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Plan Comparison */}
      {plans.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <CreditCard className="h-4 w-4 text-muted-foreground" />
              Plan Comparison
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`grid gap-y-3 text-sm`} style={{ gridTemplateColumns: `repeat(${plans.length + 1}, minmax(0, 1fr))` }}>
              <div className="font-medium">Feature</div>
              {plans.map((p) => (
                <div key={p.slug} className={`font-medium text-center ${p.slug === plan ? 'text-violet-400' : ''}`}>
                  {p.name}
                </div>
              ))}

              <div className="text-muted-foreground">Price</div>
              {plans.map((p) => (
                <div key={p.slug} className={`text-center ${p.slug === plan ? 'text-violet-400' : ''}`}>
                  {formatPrice(p.price_cents, p.currency)} / {p.billing_period}
                </div>
              ))}

              {featureKeys.map((key) => (
                <>
                  <div key={key} className="text-muted-foreground">{LIMIT_LABELS[key] || key}</div>
                  {plans.map((p) => (
                    <div key={`${p.slug}-${key}`} className={`text-center ${p.slug === plan ? 'text-violet-400' : ''}`}>
                      {formatLimit(p.limits?.[key] ?? 0)}
                    </div>
                  ))}
                </>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* CHECKPOINT: PRD-10 FR-10.10.2 — Payment history table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Receipt className="h-4 w-4 text-muted-foreground" />
            Payment History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {payments.length === 0 ? (
            <p className="text-sm text-muted-foreground">No payments yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left font-medium text-muted-foreground py-2 pr-4">Date</th>
                    <th className="text-left font-medium text-muted-foreground py-2 pr-4">Description</th>
                    <th className="text-left font-medium text-muted-foreground py-2 pr-4">Status</th>
                    <th className="text-right font-medium text-muted-foreground py-2 pr-4">Amount</th>
                    <th className="text-right font-medium text-muted-foreground py-2">Invoice</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((payment) => (
                    <tr key={payment.id} className="border-b border-border/50 last:border-0">
                      <td className="py-2 pr-4 whitespace-nowrap">
                        {new Date(payment.date).toLocaleDateString()}
                      </td>
                      <td className="py-2 pr-4">{payment.description || payment.payment_type}</td>
                      <td className="py-2 pr-4">
                        <span className={paymentStatusClass(payment.status)}>
                          {payment.status}
                        </span>
                      </td>
                      <td className="py-2 pr-4 text-right">
                        {formatPrice(payment.amount_cents, payment.currency)}
                      </td>
                      <td className="py-2 text-right">
                        {payment.invoice_url ? (
                          <a
                            href={payment.invoice_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center text-primary hover:underline"
                          >
                            View <ExternalLink className="ml-1 h-3 w-3" />
                          </a>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* CHECKPOINT: PRD-10 FR-10.10.1 Accessible cancellation feedback flow. */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent className="max-w-lg rounded-2xl border-border/70 bg-card p-0 overflow-hidden">
          <div className="border-b border-border/60 px-6 py-5">
            <DialogHeader>
              <DialogTitle>{cancelStep === 1 ? 'What led to your decision?' : 'Confirm cancellation'}</DialogTitle>
            </DialogHeader>
            <p className="mt-2 text-sm text-muted-foreground">
              {cancelStep === 1
                ? 'One answer helps us understand where PurpleToad Mail fell short.'
                : 'Your subscription will not renew. Your paid access continues through the date below.'}
            </p>
          </div>

          {cancelStep === 1 ? (
            <div className="space-y-5 px-6 py-5">
              <RadioGroup value={cancelFeedback} onValueChange={(value) => setCancelFeedback(value as CancellationFeedback)}>
                {CANCELLATION_REASONS.map((reason) => (
                  <Label
                    key={reason.value}
                    htmlFor={`cancel-${reason.value}`}
                    className="flex cursor-pointer items-center gap-3 rounded-lg border border-border/60 px-4 py-3 text-sm transition-colors hover:bg-muted/40"
                  >
                    <RadioGroupItem id={`cancel-${reason.value}`} value={reason.value} />
                    {reason.label}
                  </Label>
                ))}
              </RadioGroup>
              <div className="space-y-2">
                <Label htmlFor="cancellation-comment">Anything else? <span className="text-muted-foreground">{cancelFeedback === 'other' ? '(required)' : '(optional)'}</span></Label>
                <Textarea
                  id="cancellation-comment"
                  value={cancelComment}
                  onChange={(event) => setCancelComment(event.target.value)}
                  maxLength={1000}
                  placeholder="A short note helps us act on the feedback."
                  className="min-h-24 resize-none"
                />
              </div>
              {cancelError && <p role="alert" className="text-sm text-destructive">{cancelError}</p>}
            </div>
          ) : (
            <div className="space-y-4 px-6 py-5">
              <div className="rounded-xl border border-border/60 bg-muted/25 p-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Access ends</p>
                <p className="mt-1 text-lg font-semibold">
                  {subscription?.current_period_end
                    ? new Date(subscription.current_period_end).toLocaleDateString()
                    : 'End of the current billing period'}
                </p>
              </div>
              <p className="text-sm text-muted-foreground">
                You can keep using your mailboxes and API until then. Dodo Payments will stop future renewal charges.
              </p>
            </div>
          )}

          <DialogFooter className="border-t border-border/60 bg-muted/15 px-6 py-4 sm:justify-between">
            {cancelStep === 1 ? (
              <>
                <Button variant="outline" onClick={() => setShowCancelDialog(false)}>Keep subscription</Button>
                <Button onClick={() => {
                  if (!cancelFeedback || (cancelFeedback === 'other' && !cancelComment.trim())) {
                    setCancelError(cancelFeedback ? 'Tell us a little more when choosing Other.' : 'Choose the reason that best matches your decision.')
                    return
                  }
                  setCancelError(null)
                  setCancelStep(2)
                }}>Continue</Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setCancelStep(1)}>Back</Button>
                <Button variant="destructive" onClick={handleCancelSubscription} disabled={cancelLoading}>
                  {cancelLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Cancel subscription
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CHECKPOINT: PRD-13 FR-13.8.1 Refund through account closure. */}
      <Dialog open={showRefundDialog} onOpenChange={setShowRefundDialog}>
        <DialogContent className="max-w-md rounded-2xl border-border/70 bg-card">
          <DialogHeader>
            <DialogTitle>Request a full refund and close your account?</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm text-muted-foreground">
            <p>
              You will receive {formatPrice(subscription?.refund_amount_cents || 0, subscription?.refund_currency || 'USD')} when Dodo confirms the refund.
            </p>
            <div className="rounded-xl border border-destructive/25 bg-destructive/5 p-4">
              <p className="font-medium text-foreground">What happens next</p>
              <ul className="mt-2 list-disc space-y-1 pl-4">
                <li>Paid service ends when the refund succeeds.</li>
                <li>Your data stays available for export for 30 days.</li>
                <li>Returning after the refund requires a new paid plan.</li>
              </ul>
            </div>
          </div>
          <DialogFooter className="sm:justify-between">
            <Button variant="outline" onClick={() => setShowRefundDialog(false)}>Keep my account</Button>
            <Button variant="destructive" onClick={handleRequestRefund} disabled={refundLoading}>
              {refundLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Request refund
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function paymentStatusClass(status: string) {
  switch (status) {
    case 'succeeded':
      return 'text-xs px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
    case 'refunded':
      return 'text-xs px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-400 border border-blue-500/20'
    case 'failed':
      return 'text-xs px-2 py-0.5 rounded-full bg-red-500/10 text-red-400 border border-red-500/20'
    default:
      return 'text-xs px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/20'
  }
}

// CHECKPOINT: PRD-13 FR-13.7 — Storage add-on slider with min/step/max and base-subscription billing
function StorageAddOnControl({
  packs,
  addons,
  value,
  onChange,
  current,
  loadingId,
  onPurchase,
  onChangeAddon,
  onCancelAddon,
}: {
  packs: OveragePack[]
  addons: AddOn[]
  value: number
  onChange: (value: number) => void
  current: number
  loadingId: string | null
  onPurchase: (packType: string, quantity: number) => void
  onChangeAddon: (addonId: string, quantity: number) => void
  onCancelAddon: (addonId: string) => void
}) {
  const pack = packs.find((p) => p.pack_type === 'storage_per_gb')
  if (!pack) return null

  const activeAddon = addons.find(
    (a) => a.pack_type === 'storage_per_gb' && a.status === 'active' && !a.cancelled_at
  )
  const pendingAddon = addons.find(
    (a) => a.pack_type === 'storage_per_gb' && a.status === 'pending' && !a.cancelled_at
  )
  const currentAddon = activeAddon || pendingAddon

  const min = pack.min_quantity ?? 10
  const max = pack.max_quantity ?? 500
  const step = 10
  const quantity = Math.max(min, Math.min(max, Math.round(value / step) * step))
  const totalCents = pack.price_cents * quantity
  const isChanged = currentAddon ? quantity !== currentAddon.quantity : quantity >= min
  const isLoading = currentAddon ? loadingId === currentAddon.id : loadingId === pack.pack_type
  const isPending = !!pendingAddon
  const periodLabel = pack.billing_period === 'year' ? '/year' : '/month'

  return (
    <div className="space-y-4 rounded-lg border border-border/50 p-4">
      {pendingAddon && (
        <div className="flex items-start gap-3 rounded-md border border-amber-500/20 bg-amber-500/10 p-3 text-sm">
          <Clock className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
          <div>
            <p className="font-medium text-amber-300">Payment confirmation pending</p>
            <p className="text-xs text-amber-200/80 mt-0.5">
              Your +{pendingAddon.quantity} GB storage add-on is waiting for Dodo Payments to confirm the
              payment. This usually takes a few seconds. You can keep using the dashboard.
            </p>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h4 className="font-medium">Extra Storage</h4>
          <p className="text-xs text-muted-foreground">
            {formatPrice(pack.price_cents, pack.currency)} / GB · billed {pack.billing_period || 'yearly'}
          </p>
        </div>
        <div className="text-right">
          <div className="text-sm font-medium">{formatPrice(totalCents, pack.currency)}{periodLabel}</div>
          <div className="text-xs text-muted-foreground">{quantity} GB</div>
        </div>
      </div>

      <Slider
        value={[quantity]}
        min={min}
        max={max}
        step={step}
        onValueChange={([v]) => onChange(v)}
        disabled={isLoading || isPending}
      />

      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">
          Current extra storage: {current.toLocaleString()} GB
        </span>
        {currentAddon ? (
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={() => onChangeAddon(currentAddon.id, quantity)}
              disabled={!isChanged || isLoading || isPending}
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>Update to {formatPrice(totalCents, pack.currency)}{periodLabel}</>
              )}
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="border-red-500/20 text-red-400 hover:bg-red-500/10"
              onClick={() => onCancelAddon(currentAddon.id)}
              disabled={isLoading}
            >
              Cancel
            </Button>
          </div>
        ) : (
          <Button
            size="sm"
            onClick={() => onPurchase(pack.pack_type, quantity)}
            disabled={isLoading}
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>Add storage for {formatPrice(totalCents, pack.currency)}{periodLabel}</>
            )}
          </Button>
        )}
      </div>
    </div>
  )
}

// CHECKPOINT: PRD-13 FR-13.7 — One-time email packs and extra domain purchase cards
function OneTimePackGrid({
  packs,
  loadingId,
  onPurchase,
}: {
  packs: OveragePack[]
  loadingId: string | null
  onPurchase: (packType: string, quantity: number) => void
}) {
  const oneTimePacks = packs.filter(
    (p) => p.billing_frequency === 'one_time' && p.pack_type !== 'storage_addon_20gb' && p.pack_type !== 'storage_addon_100gb'
  )

  if (oneTimePacks.length === 0) {
    return <p className="text-sm text-muted-foreground">No one-time add-ons available.</p>
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {oneTimePacks.map((pack) => {
        const isDomain = pack.pack_type === 'extra_domain'
        const credit = Object.values(pack.credits || {})[0] ?? 0
        const creditLabel = isDomain
          ? `+${credit} domain slot${credit === 1 ? '' : 's'}`
          : `+${credit.toLocaleString()} emails`
        const isLoading = loadingId === pack.pack_type

        return (
          <div
            key={pack.pack_type}
            className="flex flex-col justify-between rounded-lg border border-border/50 bg-card/30 p-4"
          >
            <div>
              <div className="flex items-center gap-2 mb-2">
                {isDomain ? (
                  <Globe className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <Mail className="h-4 w-4 text-muted-foreground" />
                )}
                <h4 className="font-medium text-sm">{pack.name}</h4>
              </div>
              <p className="text-xs text-muted-foreground mb-3">
                {pack.description || creditLabel}
              </p>
            </div>
            <div className="flex items-center justify-between gap-3 mt-auto">
              <span className="text-sm font-medium">{formatPrice(pack.price_cents, pack.currency)}</span>
              <Button
                size="sm"
                onClick={() => onPurchase(pack.pack_type, 1)}
                disabled={isLoading}
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  'Purchase'
                )}
              </Button>
            </div>
          </div>
        )
      })}
    </div>
  )
}

function LimitRow({ label, used, limit }: { label: string; used: number; limit: number }) {
  const isUnlimited = limit < 0
  const pct = isUnlimited ? 0 : Math.min(100, Math.round((used / limit) * 100))
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-medium">
          {used.toLocaleString()}{!isUnlimited && ` / ${limit.toLocaleString()}`} {isUnlimited && '(unlimited)'}
        </span>
      </div>
      {!isUnlimited && <Progress value={pct} className="h-1.5" />}
    </div>
  )
}
